// Variabili di flow: i campi con `exportTo` che confluiscono in un nome unico.
//
// Il problema che risolvono: lo stesso dato raccolto da rami alternativi.
//
//   ricezione-point    → answers['ricezione-point'].statoRicezioneId
//   ricezione-officina → answers['ricezione-officina'].statoRicezioneId
//
// Alla fine serve UN posto dove leggerlo senza sapere da dove è passato
// l'utente. Con `exportTo: 'statoRicezioneId'` su entrambi i campi, quel posto
// è `vars.statoRicezioneId`.
//
// SCELTA CENTRALE: le variabili sono DERIVATE, non scritte.
//
// La strada ovvia sarebbe stata scriverle a ogni "Avanti". Non regge il tasto
// Indietro: l'utente compila il ramo Point, torna indietro, cambia reparto e
// prosegue da Officina — e in `vars` resterebbe il valore di Point, scritto da
// un percorso che l'utente non sta più facendo. Servirebbe disfare le
// scritture, sapere quale nodo ha scritto cosa, e gestire due nodi che
// esportano lo stesso nome.
//
// Derivarle elimina il problema alla radice: si ricalcolano dalle risposte dei
// nodi EFFETTIVAMENTE ATTRAVERSATI, quindi non esiste nessun valore residuo
// che possa raccontare un percorso abbandonato. Per questo `vars` è di sola
// lettura: assegnarci sopra sarebbe uno stato che la derivazione, un istante
// dopo, cancellerebbe.
//
// Il percorso, e non tutti i nodi: `answers` conserva anche le risposte dei
// rami abbandonati (tornando indietro l'utente deve ritrovare quello che aveva
// scritto), quindi guardare `answers` per intero riporterebbe esattamente la
// staleness che si voleva evitare.

import type { FlowActionSpec, FlowDefinition, FlowField, FlowNode, FlowVarSeed } from './flowTypes'
import { getScopeValue } from './scope'

/** Root ammesse in `from` di una variabile iniziale: solo ciò che la pagina fornisce. */
const ROOT_INIZIALI = ['env.', 'inputs.']

/** `from` valido? Deve puntare a un valore che esiste già prima che il wizard parta. */
export function eFromIniziale(from: string | undefined): boolean {
  return typeof from === 'string' && ROOT_INIZIALI.some((r) => from.startsWith(r))
}

/**
 * Chiave riservata dentro la risposta di un nodo, dove finiscono le variabili
 * scritte dall'azione "Imposta variabile".
 *
 * Sta DENTRO la risposta del nodo e non in un contenitore globale per la
 * stessa ragione per cui i campi si derivano dal percorso: così una variabile
 * scritta su un ramo poi abbandonato sparisce da sola insieme al nodo che
 * l'aveva scritta, senza bisogno di disfare niente.
 */
export const NODE_VARS_KEY = '$vars'

/** Un nome di variabile è un identificatore singolo, niente path annidati. */
const VAR_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/

/**
 * Normalizza ciò che l'admin ha scritto in `exportTo`.
 * `statoRicezioneId` e `vars.statoRicezioneId` sono la stessa variabile;
 * qualunque altra forma è un errore, e `validateFlow` lo segnala.
 */
export function normalizeVarName(exportTo: string | undefined): string | null {
  if (typeof exportTo !== 'string') return null
  const name = exportTo.trim().replace(/^vars\./, '')
  return VAR_NAME.test(name) ? name : null
}

/**
 * Un valore "non c'è". Stesso metro di `isMissing` in `compileFlow`: lo `0` è
 * una risposta legittima, non un vuoto.
 */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (value === '') return true
  if (Array.isArray(value)) return value.length === 0
  return false
}

/**
 * Calcola le variabili del flow lungo un percorso.
 *
 * `path` sono gli id dei nodi attraversati, in ordine, corrente compreso.
 * Fra due nodi dello stesso percorso che esportano lo stesso nome vince
 * l'ULTIMO valorizzato: è quello che l'utente ha compilato più di recente.
 * Un nodo attraversato ma lasciato vuoto non cancella quanto già raccolto —
 * un campo opzionale saltato non è una richiesta di dimenticare.
 */
export function deriveFlowVars(
  flow: Pick<FlowDefinition, 'nodes' | 'vars'>,
  answers: Record<string, unknown>,
  path: readonly string[],
  daPagina: { env?: Record<string, unknown>; inputs?: Record<string, unknown> } = {},
): Record<string, unknown> {
  // Le variabili iniziali stanno in TESTA: sono il livello più basso, e ciò che
  // l'utente risponde lungo il percorso le sovrascrive. L'ordine inverso
  // significherebbe che un valore precompilato cancella una risposta data.
  const out: Record<string, unknown> = seminaVars(flow.vars, daPagina)
  if (!path.length) return out

  const byId = new Map<string, FlowNode>()
  for (const node of flow.nodes ?? []) byId.set(node.id, node)

  for (const nodeId of path) {
    const node = byId.get(nodeId)
    if (!node) continue

    const answer = answers[nodeId] as Record<string, unknown> | undefined

    for (const field of node.fields ?? []) {
      const name = normalizeVarName(field.exportTo)
      if (!name) continue
      const value = answer?.[field.name]
      if (isEmpty(value)) continue
      out[name] = value
    }

    // Le variabili scritte dalle azioni vengono DOPO i campi dello stesso nodo:
    // le azioni girano al click su "Avanti", cioè quando i campi sono già
    // compilati, e devono poterne calcolare o correggere il valore.
    //
    // A differenza dei campi si scrivono anche se vuote: un'azione è una
    // decisione esplicita di chi configura il flow, e per "solo se valorizzato"
    // c'è la condizione `when` dell'azione.
    Object.assign(out, leggiVarDelNodo(answer))
  }

  return out
}

/** Risolve le variabili iniziali contro i valori che la pagina ha fornito. */
function seminaVars(
  seeds: FlowVarSeed[] | undefined,
  daPagina: { env?: Record<string, unknown>; inputs?: Record<string, unknown> },
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!seeds?.length) return out

  // Scope minimo: solo ciò che esiste prima dell'avvio. `answers` e `vars` non
  // ci sono di proposito — leggerli qui creerebbe una dipendenza circolare con
  // la derivazione stessa.
  const scope = { env: daPagina.env ?? {}, inputs: daPagina.inputs ?? {} }

  for (const seed of seeds) {
    const name = normalizeVarName(seed?.name)
    if (!name || !eFromIniziale(seed.from)) continue
    const value = getScopeValue(scope, seed.from)
    // Un valore assente non crea la chiave: `vars.x` inesistente e `vars.x`
    // vuota si comportano diversamente in `not_empty`, e una variabile
    // dichiarata ma non fornita dalla pagina è la prima.
    if (value === undefined || value === null || value === '') continue
    out[name] = value
  }

  return out
}

/** Variabili scritte dalle azioni dentro la risposta di un nodo. */
function leggiVarDelNodo(answer: Record<string, unknown> | undefined): Record<string, unknown> {
  const bucket = answer?.[NODE_VARS_KEY]
  if (bucket === null || typeof bucket !== 'object') return {}
  return bucket as Record<string, unknown>
}

/**
 * Scrive una variabile nel contenitore del nodo indicato, creandolo se serve.
 * Muta sul posto: `FlowFormStep` riscrive la risposta del nodo con uno spread,
 * che conserva la chiave riservata.
 */
export function setNodeVar(
  answers: Record<string, unknown>,
  nodeId: string,
  name: string,
  value: unknown,
): void {
  const answer = (answers[nodeId] ?? (answers[nodeId] = {})) as Record<string, unknown>
  const bucket = (answer[NODE_VARS_KEY] ?? (answer[NODE_VARS_KEY] = {})) as Record<string, unknown>
  bucket[name] = value
}

export interface FlowVarSource {
  name: string
  /** Da dove arriva: un campo, un'azione di un passo, o una variabile iniziale. */
  origine: 'campo' | 'azione' | 'iniziale'
  /** Come mostrarla all'admin ("Ricezione Point", "Reparto (azione)", ...). */
  etichetta: string
  /** Vuoti per le variabili iniziali: non appartengono a nessun passo. */
  nodeId: string
  nodeTitle: string
  /** Presente solo se la variabile arriva da un CAMPO. */
  field?: FlowField
  /** Il nodo che la esporta può essere saltato: la variabile può mancare. */
  skippable: boolean
}

/** Tutti i punti in cui una variabile viene alimentata. Serve al builder e a `validateFlow`. */
export function collectVarSources(
  flow: Pick<FlowDefinition, 'nodes' | 'vars'>,
): FlowVarSource[] {
  const out: FlowVarSource[] = []

  for (const seed of flow.vars ?? []) {
    const name = normalizeVarName(seed?.name)
    if (!name) continue
    out.push({
      name,
      origine: 'iniziale',
      etichetta: `valore iniziale (${seed.from})`,
      nodeId: '',
      nodeTitle: '',
      skippable: false,
    })
  }

  for (const node of flow.nodes ?? []) {
    const skippable = node.skip_when !== undefined
    const titolo = node.title || node.id

    for (const field of node.fields ?? []) {
      const name = normalizeVarName(field.exportTo)
      if (!name) continue
      out.push({
        name, origine: 'campo', etichetta: titolo,
        nodeId: node.id, nodeTitle: titolo, field, skippable,
      })
    }

    for (const name of varDaAzioni(node.on_next)) {
      out.push({
        name, origine: 'azione', etichetta: `${titolo} (azione)`,
        nodeId: node.id, nodeTitle: titolo, skippable,
      })
    }
  }

  return out
}

/** Nomi delle variabili impostate dalle azioni `set_var` di un nodo. */
function varDaAzioni(actions: FlowActionSpec[] | undefined): string[] {
  const out: string[] = []
  for (const action of actions ?? []) {
    if (action?.type !== 'set_var') continue
    const name = normalizeVarName(action.config?.nome as string | undefined)
    if (name) out.push(name)
  }
  return out
}

/** Nomi delle variabili dichiarate, ordinati. Per le tendine di suggerimento. */
export function collectVarNames(flow: Pick<FlowDefinition, 'nodes' | 'vars'>): string[] {
  return [...new Set(collectVarSources(flow).map((s) => s.name))].sort()
}
